import { Controller } from "@hotwired/stimulus"
import { rrFetch } from "../../utils/rr_fetch"
import { getViewportTier } from "../../utils"

export default class AnalyticsController extends Controller {
  static targets = ["input", "button"]
  static values = {
    attemptId: String,
    csrfToken: String,
  }

  endpoint = "/analytics/event"

  connect() {
    this.viewport = getViewportTier()
  }

  handleEvent(e) {
    const el = e.currentTarget
    const events = this.extractEvents(el) // [{ idx:1, name:"..." }, { idx:2, name:"..." }, ...]

    if (events.length === 0) return

    if (el.dataset.sent === "1") return
    el.dataset.sent = "1"

    const base = {
      page: window.location.pathname,
      ts: Date.now(),
    }

    for (let i = 0; i < events.length; i++) {
      const { idx, name } = events[i]
      this.send(
        {
          event: name,
          page: base.page,
          ts: base.ts + i,
        },
        el,
        idx
      )
    }
  }

  // Finds: data-event, data-event2, data-event3, ...
  extractEvents(el) {
    const out = []
    if (!el?.attributes) return out

    for (const attr of el.attributes) {
      const name = attr.name // "data-event", "data-event-2"
      if (!name.startsWith("data-event")) continue

      // match: data-event OR data-event-<n>
      const m = name.match(/^data-event(?:-(\d+))?$/)
      if (!m) continue

      const idx = m[1] ? Number.parseInt(m[1], 10) : 1
      if (!Number.isFinite(idx) || idx < 1) continue

      const val = (attr.value || "").trim()
      if (!val) continue

      out.push({ idx, name: val })
    }

    out.sort((a, b) => a.idx - b.idx)
    return out
  }

  send(payload, el, idx = 1) {
    try {
      const formData = new FormData()
      formData.append("csrf_token", this.csrfTokenValue || "")
      formData.append("event", payload.event)
      formData.append("viewport", this.viewport)
      formData.append("page", payload.page)
      formData.append("ts", String(payload.ts))

      // meta for this specific event index:
      // data-analytics-1-meta-*, data-analytics-2-meta-*, ...
      this.appendIndexedMeta(formData, el, idx)

      // customIDs for this specific event index:
      this.appendCustomIDs(formData, idx)

      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
        const raw = (el.value ?? "").toString().trim()
        if (raw) formData.append("meta.input_value", raw)
      }

      if (navigator.sendBeacon) {
        const ok = navigator.sendBeacon(this.endpoint, formData)
        if (ok) return
      }

      rrFetch(this.endpoint, {
        method: "POST",
        body: formData,
        keepalive: true,
      }).catch(() => { })
    } catch (_) {
      // ignore
    }
  }

  appendIndexedMeta(formData, el, idx) {
    if (!el?.attributes) return

    const prefix = idx === 1
      ? "data-analytics-meta-"
      : `data-analytics-${idx}-meta-`

    for (const attr of el.attributes) {
      const name = attr.name
      if (!name.startsWith(prefix)) continue

      const key = name.slice(prefix.length)
      const val = attr.value
      if (!key || val == null || val === "") continue

      formData.append(`meta.${key}`, String(val))
    }
  }

  appendCustomIDs(formData, idx = 1) {
  const wrapper = this.element
  if (!wrapper?.attributes) return

  const prefix = idx === 1
    ? "data-analytics-customid-"
    : `data-analytics-${idx}-customid-`

  for (const attr of wrapper.attributes) {
    const name = attr.name
    if (!name.startsWith(prefix)) continue

    const key = name.slice(prefix.length)
    const val = attr.value
    if (!key || val == null || val === "") continue

    formData.append(`cid.${key}`, String(val))
  }
}
}

